/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useState, useMemo } from 'react';
import { Platform } from '@staffino/primitives';
import TextInput from './text-input';
import TextArea from './text-area';
import Paragraph from './paragraph';
import convertCssUnit from './convert-css-unit';

type Props = {
  type?: string;
  invalid?: boolean;
  activeColor?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  name?: string;
  limit?: number;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onFocus?: () => void;
};

const TextInputField = React.forwardRef(
  (
    {
      type,
      invalid,
      activeColor,
      value,
      onChange,
      name,
      placeholder,
      limit,
      onKeyDown,
      onFocus,
    }: Props,
    ref: any,
  ) => {
    const [active, setActive] = useState(false);
    let borderColor = 'separator';
    if (active && activeColor) {
      borderColor = activeColor;
    }
    if (invalid) {
      borderColor = 'notification';
    }
    const charactersLeftColor =
      limit &&
      value &&
      (value?.length >= limit - 100 || (active && !value && limit <= 100))
        ? 'lightText'
        : 'transparent';

    const handleChange = useMemo(
      () =>
        Platform.select<(v) => void>({
          default: (event) => onChange?.(event?.target.value),
          native: (val) => onChange?.(val),
        }),
      [onChange],
    );

    return type === 'MultiLine' ? (
      <>
        <TextArea
          ref={ref}
          name={name}
          maxLength={limit}
          py={convertCssUnit('0.865rem')}
          px={convertCssUnit('0.625rem')}
          minRows={3}
          borderColor={borderColor}
          onFocus={() => {
            setActive(true);
            onFocus?.();
          }}
          onBlur={() => setActive(false)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          onChange={handleChange}
        />
        <Paragraph textAlign="right" color={charactersLeftColor}>
          {limit ? `${limit - (value?.length ?? 0)} characters left` : null}
        </Paragraph>
      </>
    ) : (
      <TextInput
        ref={ref}
        name={name}
        maxLength={limit}
        p={convertCssUnit('0.625rem')}
        borderColor={borderColor}
        onFocus={() => {
          setActive(true);
          onFocus?.();
        }}
        onBlur={() => setActive(false)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        onChange={handleChange}
      />
    );
  },
);

export default TextInputField;
